/**
 * Finalize Details Panel
 *
 * Presentational component for editing final LoR fields (policy, claim,
 * adjuster contact). It renders inline editable rows and a generate button.
 * All state and persistence actions are delegated to the parent component.
 */
import React from 'react';

import { EditableFieldRow } from '@/components/claims-filing/common/EditableFieldRow';
import { FieldSection } from '@/components/claims-filing/common/FieldSection';

type FinalizeValues = {
  policyNumber: string;
  claimNumber: string;
  adjusterName: string;
  adjusterEmail: string;
  adjusterFax: string;
  adjusterPhone: string;
};

type Props = {
  values: FinalizeValues;
  hasUnsavedChanges: boolean;
  noTemplatesAvailable?: boolean;
  evidenceByKey: Record<string, boolean>;
  onHover: (key: string | null) => void;
  onChange: (field: keyof FinalizeValues, value: string) => void;
  onSave: {
    savePolicy: () => void;
    saveClaim: () => void;
    saveAdjName: () => void;
    saveAdjEmail: () => void;
    saveAdjFax: () => void;
    saveAdjPhone: () => void;
  };
  onCancel: {
    cancelPolicy: () => void;
    cancelClaim: () => void;
    cancelAdjName: () => void;
    cancelAdjEmail: () => void;
    cancelAdjFax: () => void;
    cancelAdjPhone: () => void;
  };
  onGenerate: () => void;
  isGenerateDisabled: boolean;
  isGenerating: boolean;
};

export const FinalizeDetailsPanel: React.FC<Props> = ({
  values,
  hasUnsavedChanges,
  noTemplatesAvailable = false,
  evidenceByKey,
  onHover,
  onChange,
  onSave,
  onCancel,
  onGenerate,
  isGenerateDisabled,
  isGenerating,
}) => {
  return (
    <FieldSection className="border-0">
      <EditableFieldRow
        id="final:policy"
        label="Policy Number"
        value={values.policyNumber}
        required
        hoverKey="final:policy"
        hasEvidence={evidenceByKey['final:policy']}
        onHover={onHover}
        onChange={(next: string) => onChange('policyNumber', next)}
        onSave={onSave.savePolicy}
        onCancel={onCancel.cancelPolicy}
        mode="editable"
        size="compact"
      />
      <EditableFieldRow
        id="final:claim"
        label="Claim Number"
        value={values.claimNumber}
        required
        hoverKey="final:claim"
        hasEvidence={evidenceByKey['final:claim']}
        onHover={onHover}
        onChange={(next: string) => onChange('claimNumber', next)}
        onSave={onSave.saveClaim}
        onCancel={onCancel.cancelClaim}
        mode="editable"
        size="compact"
      />
      <EditableFieldRow
        id="final:adjName"
        label="Adjuster Name"
        value={values.adjusterName}
        required
        hoverKey="final:adjName"
        hasEvidence={evidenceByKey['final:adjName']}
        onHover={onHover}
        onChange={(next: string) => onChange('adjusterName', next)}
        onSave={onSave.saveAdjName}
        onCancel={onCancel.cancelAdjName}
        mode="editable"
        size="compact"
      />
      <EditableFieldRow
        id="final:adjEmail"
        label="Adjuster Email"
        value={values.adjusterEmail}
        required
        hoverKey="final:adjEmail"
        hasEvidence={evidenceByKey['final:adjEmail']}
        onHover={onHover}
        onChange={(next: string) => onChange('adjusterEmail', next)}
        onSave={onSave.saveAdjEmail}
        onCancel={onCancel.cancelAdjEmail}
        mode="editable"
        size="compact"
      />
      <EditableFieldRow
        id="final:adjFax"
        label="Adjuster Fax"
        value={values.adjusterFax}
        required
        hoverKey="final:adjFax"
        hasEvidence={evidenceByKey['final:adjFax']}
        onHover={onHover}
        onChange={(next: string) => onChange('adjusterFax', next)}
        onSave={onSave.saveAdjFax}
        onCancel={onCancel.cancelAdjFax}
        mode="editable"
        size="compact"
      />
      <EditableFieldRow
        id="final:adjPhone"
        label="Adjuster Phone"
        value={values.adjusterPhone}
        hoverKey="final:adjPhone"
        hasEvidence={evidenceByKey['final:adjPhone']}
        onHover={onHover}
        onChange={(next: string) => onChange('adjusterPhone', next)}
        onSave={onSave.saveAdjPhone}
        onCancel={onCancel.cancelAdjPhone}
        mode="editable"
        size="compact"
      />
      {(hasUnsavedChanges || noTemplatesAvailable) && (
        <div className="mt-3 flex items-center justify-end gap-2 p-3">
          {hasUnsavedChanges && (
            <span className="text-xs text-gray-500 mr-auto">
              Unsaved changes
            </span>
          )}
          {!hasUnsavedChanges && noTemplatesAvailable && (
            <span className="text-xs text-gray-500 mr-auto">
              No LoR templates are available. You can review details, but
              generating a letter isn’t possible until a template is added.
            </span>
          )}
        </div>
      )}
    </FieldSection>
  );
};
